/**
 * Reviews (WALLIYO-0131): the other end of a grant.
 *
 * Two lists and a lookup. What is held by name with no end date; which leads
 * owe their people a look; and, underneath, "who can do this" — the reverse
 * question, asked of one action. They belong on the same tab because they are
 * the same worry from two directions: what somebody holds that nobody
 * remembers, and who holds a thing worth knowing about.
 *
 * Nothing is revoked here. Revoking is a change to somebody's permissions, it
 * lives on Permissions and is audited there, and a second path to the same
 * write is a second thing to keep right.
 */

import type { GrantToReview, LeadOwingReview } from "@wallyo/contracts/console/hr";
import { settingName, t, type MessageId } from "../../i18n";
import { formatDateTime } from "../../i18n/format";
import { ErrorNotice } from "../../shell/ErrorNotice";
import { hrHref } from "./hr-route";

export type ReviewsState =
  | { status: "loading"; data: ReviewsData | null }
  | { status: "ready"; data: ReviewsData }
  | { status: "failed"; data: ReviewsData | null; error: unknown };

export type ReviewsData = {
  grants: readonly GrantToReview[];
  leads: readonly LeadOwingReview[];
};

const CONCERN_LABEL: Record<GrantToReview["concern"], MessageId> = {
  holder_left: "reviews.concern.holderLeft",
  never_ends: "reviews.concern.neverEnds",
  never_used: "reviews.concern.neverUsed",
};

/** Access belonging to nobody is worse than access that merely never ends. */
const CONCERN_TONE: Record<GrantToReview["concern"], string> = {
  holder_left: "pill bad",
  never_ends: "pill warn",
  never_used: "pill",
};

function Grants({ grants }: { grants: readonly GrantToReview[] }) {
  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th>{t("reviews.col.who")}</th>
            <th>{t("reviews.col.what")}</th>
            <th>{t("reviews.col.why")}</th>
            <th>{t("reviews.col.concern")}</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {grants.map((grant) => (
            <tr key={`${grant.staffMemberId}:${grant.settingId}`}>
              <td>
                <a href={hrHref({ uuid: grant.uuid })}>{grant.name}</a>
                {!grant.stillHere ? (
                  <div>
                    <span className="pill bad">{t("leaving.alreadyLeft")}</span>
                  </div>
                ) : null}
              </td>
              <td>
                <strong>{settingName(grant.settingId)}</strong>
              </td>
              <td>
                {grant.reason ?? <span className="foot">{t("leaving.noReason")}</span>}
                <div className="foot">{t("reviews.changedAt", { when: formatDateTime(grant.changedAt) })}</div>
              </td>
              <td>
                <span className={CONCERN_TONE[grant.concern]}>{t(CONCERN_LABEL[grant.concern])}</span>
              </td>
              <td className="num">
                <a className="btn quiet small" href={`#permissions/employee/${grant.uuid}`}>
                  {t("reviews.open")}
                </a>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function ReviewsView(props: { state: ReviewsState; onRetry: () => void; who: React.ReactNode }) {
  const { state } = props;
  const data = state.data;

  return (
    <>
      {state.status === "failed" ? (
        <div className="audit-error">
          <p className="foot">{t("hr.failed")}</p>
          <ErrorNotice error={state.error} />
          <div className="btns">
            <button className="btn" type="button" onClick={props.onRetry}>
              {t("hr.retry")}
            </button>
          </div>
        </div>
      ) : null}

      {!data && state.status === "loading" ? (
        <div className="empty" role="status">
          <span className="spinner" aria-hidden="true" />
          <span>{t("hr.loading")}</span>
        </div>
      ) : null}

      {data ? (
        <>
          <section className="card">
            <h2>{t("reviews.grants.title")}</h2>
            <p className="foot">{t("reviews.grants.help")}</p>
            {data.grants.length === 0 ? (
              <p className="foot">{t("reviews.grants.none")}</p>
            ) : (
              <Grants grants={data.grants} />
            )}
          </section>

          <section className="card">
            <h2>{t("reviews.leads.title")}</h2>
            <p className="foot">{t("reviews.leads.help")}</p>
            {data.leads.length === 0 ? (
              <p className="foot">{t("reviews.leads.none")}</p>
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>{t("reviews.col.lead")}</th>
                      <th>{t("reviews.col.team")}</th>
                      <th>{t("reviews.col.people")}</th>
                      <th>{t("reviews.col.neverEnding")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.leads.map((lead) => (
                      <tr key={`${lead.staffMemberId}:${lead.teamName}`}>
                        <td>
                          <a href={hrHref({ uuid: lead.uuid })}>{lead.name}</a>
                        </td>
                        <td>{lead.teamName}</td>
                        <td className="num">{lead.people}</td>
                        <td className="num">
                          {lead.neverEnding > 0 ? (
                            <span className="pill warn">{lead.neverEnding}</span>
                          ) : (
                            lead.neverEnding
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      ) : null}

      {/* The same worry from the other direction: not what one person holds,
          but who holds one thing. */}
      <section className="card">
        <h2>{t("team.tab.who")}</h2>
        {props.who}
      </section>
    </>
  );
}
